import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams, useSearchParams } from 'react-router';
import styled from 'styled-components';

import { SmallContainer } from '@sorare/core/src/atoms/layout/Container';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useDialogParam } from '@sorare/core/src/hooks/navigation/useDialogParam';
import { useMarkNotificationsAsRead } from '@sorare/core/src/hooks/notification/useMarkNotificationsAsRead';

import { Announcement } from '#src/components/activity/Announcement/index.tsx';

import type {
  GetSpecificNewsQuery,
  GetSpecificNewsQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled.div`
  padding-bottom: var(--double-unit);
`;

const GET_SPECIFIC_NEWS_QUERY = gql`
  query GetSpecificNewsQuery($cursor: String, $announcementIds: [String!]) {
    announcements(
      after: $cursor
      announcementIds: $announcementIds
      first: 10
    ) {
      nodes {
        id
        ...Announcement_announcement
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${Announcement.fragments.announcement}
` as TypedDocumentNode<GetSpecificNewsQuery, GetSpecificNewsQueryVariables>;

type Props = {
  inDialog?: boolean;
  id: string;
};

const SpecificNewsComponent = ({ inDialog, id }: Props) => {
  const markNotificationsAsRead = useMarkNotificationsAsRead();
  const { data, loading } = useQuery(GET_SPECIFIC_NEWS_QUERY, {
    variables: {
      announcementIds: [id!],
    },
    skip: !id,
  });
  const [searchParams] = useSearchParams();
  const announcement = data?.announcements.nodes[0];
  const announcementNotificationId = searchParams.get(
    'announcementNotificationId'
  );

  useEffect(() => {
    if (announcementNotificationId) {
      markNotificationsAsRead([{ id: announcementNotificationId }]);
    }
  }, [markNotificationsAsRead, announcementNotificationId]);

  if (!data || loading) {
    return (
      <Root>
        <SmallContainer>
          <LoadingIndicator />
        </SmallContainer>
      </Root>
    );
  }

  return (
    <Root>
      <SmallContainer>
        {announcement ? (
          <Announcement announcement={announcement} inDialog={!!inDialog} />
        ) : (
          <FormattedMessage
            id="SpecificNews.notFound"
            defaultMessage="This news does not exist."
          />
        )}
      </SmallContainer>
    </Root>
  );
};

export const SpecificNewsDialogContent = ({
  ...otherProps
}: Omit<Props, 'id'>) => {
  const id = useDialogParam(DialogKey.news);
  if (!id) {
    return null;
  }
  return (
    <SpecificNewsComponent id={idFromObject(id)} {...otherProps} inDialog />
  );
};

export const SpecificNews = ({ ...otherProps }: Omit<Props, 'id'>) => {
  const { id } = useParams();
  if (!id) {
    return null;
  }
  return <SpecificNewsComponent id={idFromObject(id)} {...otherProps} />;
};
